class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.overlay = document.querySelector('.header__account-overlay');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    // Control overlay for mega menu
    if (this.overlay) {
      if (this.mainDetailsToggle.open) {
        this.overlay.classList.add('active');
      } else {
        // Check if any other mega menu is still open
        const anyMegaMenuOpen = document.querySelectorAll('.mega-menu[open]').length > 0;
        if (!anyMegaMenuOpen) {
          this.overlay.classList.remove('active');
        }
      }
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

// Extend the HeaderMenu class to add tab functionality
class MegaMenuTabs {
  constructor() {
    // Initialize on page load
    this.init();
    
    // Re-initialize when new mega menus are added (for theme editor)
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => this.init());
    }
  }
  
  init() {
    // Find all mega menu containers
    const megaMenus = document.querySelectorAll('.mega-menu');
    
    megaMenus.forEach(menu => {
      const tabs = menu.querySelectorAll('.mega-menu__tab');
      const panels = menu.querySelectorAll('.mega-menu__panel');
      
      if (!tabs.length || !panels.length) return;
      
      // Add hover listeners to each tab
      tabs.forEach((tab, index) => {
        // Hover functionality for desktop
        tab.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 990) {
            this.activateTab(tabs, panels, index);
          }
        });
        
        // Click functionality for accessibility and mobile
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          this.activateTab(tabs, panels, index);
        });
        
        // Keyboard navigation
        tab.addEventListener('keydown', (e) => {
          this.handleKeyboard(e, tabs, panels, index);
        });
      });
      
      // Set initial ARIA attributes
      this.initializeAria(tabs, panels);
    });
  }
  
  activateTab(tabs, panels, index) {
    // Remove active state from all tabs and panels
    tabs.forEach(t => {
      t.classList.remove('mega-menu__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    
    panels.forEach(p => {
      p.classList.remove('mega-menu__panel--active');
    });
    
    // Add active state to selected tab and panel
    tabs[index].classList.add('mega-menu__tab--active');
    tabs[index].setAttribute('aria-selected', 'true');
    panels[index].classList.add('mega-menu__panel--active');
    
    // Update focus for screen readers
    tabs[index].focus();
  }
  
  handleKeyboard(e, tabs, panels, currentIndex) {
    let newIndex;
    
    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        newIndex = currentIndex + 1;
        if (newIndex >= tabs.length) newIndex = 0;
        this.activateTab(tabs, panels, newIndex);
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = tabs.length - 1;
        this.activateTab(tabs, panels, newIndex);
        break;
        
      case 'Home':
        e.preventDefault();
        this.activateTab(tabs, panels, 0);
        break;
        
      case 'End':
        e.preventDefault();
        this.activateTab(tabs, panels, tabs.length - 1);
        break;
    }
  }
  
  initializeAria(tabs, panels) {
    // Set proper ARIA roles if not already set
    tabs.forEach((tab, index) => {
      if (!tab.hasAttribute('role')) {
        tab.setAttribute('role', 'tab');
      }
      if (!tab.hasAttribute('aria-controls')) {
        tab.setAttribute('aria-controls', panels[index].id);
      }
    });
    
    panels.forEach((panel, index) => {
      if (!panel.hasAttribute('role')) {
        panel.setAttribute('role', 'tabpanel');
      }
      if (!panel.hasAttribute('aria-labelledby')) {
        const tabId = `mega-menu-tab-${index}`;
        tabs[index].id = tabId;
        panel.setAttribute('aria-labelledby', tabId);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MegaMenuTabs();
  });
} else {
  new MegaMenuTabs();
}
